interface AttentionBadgeProps {
  /** Count to display. Hidden when 0. Renders "9+" when >= 10. */
  count: number;
  /**
   * Positioning mode:
   *   - "overlay" (default): absolutely positioned at top-right of parent
   *     (parent must be `relative`). Used by the bottom tab bar to overlay
   *     the Today icon.
   *   - "inline":  flows inline. Used by the Today-header pill.
   */
  position?: 'overlay' | 'inline';
  /** Optional ARIA label override. Defaults to "{count} need your attention". */
  ariaLabel?: string;
}

/**
 * AttentionBadge — small red bubble with a white number.
 * Tailwind-only, no external deps. Hidden when `count <= 0`.
 *
 * Visual lock per Chunk 8 spec:
 *   - bg-red-500
 *   - text-white text-[10px] font-bold leading-none
 *   - rounded-full min-w-5 h-5 px-1.5 (so single digit stays a circle,
 *     double digit stretches to a pill)
 *   - overlay placement: -top-1 -right-1 with a thin white ring so it pops
 *     off any background.
 */
export function AttentionBadge({
  count,
  position = 'overlay',
  ariaLabel,
}: AttentionBadgeProps): JSX.Element | null {
  if (!Number.isFinite(count) || count <= 0) return null;

  const display = count >= 10 ? '9+' : String(count);
  const label = ariaLabel ?? `${count} need your attention`;

  const base =
    'flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-white';
  const positional =
    position === 'overlay' ? 'absolute -top-1 -right-1 pointer-events-none' : 'inline-flex';

  return (
    <span
      className={`${base} ${positional}`}
      role="status"
      aria-label={label}
      data-testid="attention-badge"
    >
      {display}
    </span>
  );
}
